import {formatPercent} from 'common/util';

export enum ConversionStepBarType {
  Normal = `normal`,
  Comparison = `comparison`,
}

export interface ConversionStepBar<ExtrasT = any> {
  displayName?: string;
  /* conversion rate from the previous step in its respective funnel between 0 - 1 */
  conversionRate: number;
  /* number of conversions in this step */
  value: number;
  /* color used to override default color scheme */
  color?: string;
  type?: ConversionStepBarType;

  /** can store arbitrary data associated with the bar, will be passed through in event handlers & callbacks */
  extras?: ExtrasT;
}

// placeholder steps don't contain any data, they visually fill space so that
export interface PlaceholderStep {
  placeholder: true;
}

export interface NormalConversionStep<StepExtrasT = any, BarExtrasT = any> {
  placeholder?: false;
  bars: Array<ConversionStepBar<BarExtrasT>>;
  displayName: string;
  tooltipName?: string; // more descriptive name only shown in a tooltip on hover

  /** can store arbitrary data associated with the conversion step, will be passed through in event handlers & callbacks */
  extras?: StepExtrasT;
}

export type ConversionStep<StepExtrasT = any, BarExtrasT = any> =
  | PlaceholderStep
  | NormalConversionStep<StepExtrasT, BarExtrasT>;

// layout type should not be used outside the component
export interface ConversionStepBarLayout {
  heightPercent: number; // in range [0, 1]
  dropoffHeightPercent: number; // in range [0, 1]
}

// layout type should not be used outside the component
export interface ConversionStepLayout {
  barLayouts: ConversionStepBarLayout[];
}

// throws errors if validation fails
export function validateConversionSteps(conversionSteps: ConversionStep[]): void {
  if (!Array.isArray(conversionSteps)) {
    throw new Error(`conversionSteps must be an array`);
  }

  const normalSteps = conversionSteps.filter((step) => !step.placeholder) as NormalConversionStep[];
  if (normalSteps.length > 0) {
    const barsPerStep = normalSteps[0].bars.length;
    if (normalSteps.some((step) => step.bars.length !== barsPerStep)) {
      throw new Error(`conversionSteps must have the same number of bars`);
    }
  }
}

// It's assumed that all steps have the same number of bars, this is enforced in validateConversionSteps()
export function getNumBarsPerStep(conversionSteps: ConversionStep[]): number {
  const normalSteps = conversionSteps.filter((step) => !step.placeholder) as NormalConversionStep[];
  if (normalSteps.length > 0) {
    return normalSteps[0].bars.length;
  }
  return 0;
}

export function getMaxStepValue(steps: ConversionStep[]): number {
  const stepValues = steps.flatMap((step) => {
    if (step.placeholder !== true) {
      // `!== true` required because strict null checks are not enabled in tsconfig
      return step.bars.map((barData) => barData.value);
    }
    return [];
  });
  return stepValues.length > 0 ? Math.max(...stepValues) : 0;
}

function getFirstStepLayout(
  firstNormalStep: NormalConversionStep,
  max: number,
  valueBasedHeights: boolean,
): ConversionStepLayout {
  // the first normal step's bars are always 100% unless there are zero conversions or are using valueBasedHeights
  const stepMax = getMaxStepValue([firstNormalStep]);
  const startsOnZero = stepMax <= 0;
  const initialHeightPercent = startsOnZero ? 0 : 1;
  const barLayouts = firstNormalStep.bars.map((step) => ({
    heightPercent: valueBasedHeights ? (initialHeightPercent * step.value) / max : initialHeightPercent,
    dropoffHeightPercent: 0, // There is never any dropoff before the first step
  }));
  return {barLayouts};
}

export function getConversionStepLayouts(steps: ConversionStep[], valueBasedHeights = false): ConversionStepLayout[] {
  const stepLayouts: ConversionStepLayout[] = [];

  let previousNormalStepLayout: null | ConversionStepLayout = null; // layout for a previous normal step, ignoring layouts for previous placeholder steps
  const max = getMaxStepValue(steps);
  for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
    const step = steps[stepIndex];

    if (step.placeholder !== true) {
      // `!== true` required because strict null checks are not enabled in tsconfig
      if (previousNormalStepLayout) {
        const barLayouts = step.bars.map((bar, barIndex) => {
          const heightPercent = valueBasedHeights
            ? bar.value / max
            : Math.abs(bar.conversionRate) * previousNormalStepLayout.barLayouts[barIndex].heightPercent;
          const dropoffHeightPercent = previousNormalStepLayout.barLayouts[barIndex].heightPercent - heightPercent;
          return {heightPercent, dropoffHeightPercent};
        });
        previousNormalStepLayout = {barLayouts};
      } else {
        previousNormalStepLayout = getFirstStepLayout(step, max, valueBasedHeights);
      }
      stepLayouts.push(previousNormalStepLayout);
    } else {
      stepLayouts.push({barLayouts: []});
    }
  }

  return stepLayouts;
}

export function getPercentLabel(percent: number /* in range [0, 1] */): string {
  const percentagePoints = percent * 100;
  if (percentagePoints >= 99.5) {
    return `100%`;
  }

  return formatPercent(percent);
}
